import { elide } from "@common/utils/activationSuiteDisplay"
import { formatCollectionDay } from "@data/constants/studentsConstants"
import { formatEuros } from "../../brevet/utils/formatEuros"
import { inscriptionFormatLabel } from "../constants/inscriptionContent"
import {
    COMMITMENT_COPY,
    formatNameOfOffer,
} from "../constants/inscriptionFormats"
import { monthlyPrice, quoteIsWithoutCommitment } from "../utils/formulaChoice"

/**
 * Ce que l'écran de réservation DIT d'un devis serveur : ses mots et ses
 * montants, sans en recalculer aucun. Tout ce qui choisit un libellé selon le
 * devis vit ici, hors de l'écran, pour se tester sans le monter.
 *
 * Un devis d'avant ces champs (serveur en cours de déploiement, devis gravé)
 * ne les porte pas : chaque lecture retombe alors sur ce que l'écran disait
 * déjà, jamais sur un blanc.
 */

const euros = (cents) => formatEuros(cents == null ? null : cents / 100)

/** Les deux valeurs de la ligne « Engagement », dans les mots de l'étape formule. */
const COMMITTED = COMMITMENT_COPY.duration
const NOT_COMMITTED = COMMITMENT_COPY.none

export const commitmentLabel = (quote) =>
    quoteIsWithoutCommitment(quote) ? NOT_COMMITTED : COMMITTED

// Le volume ne se coupe pas de son unité quand le récap passe à la ligne.
export const recapLabel = (quote) =>
    `${inscriptionFormatLabel(quote.offer_type)} · ${quote.weekly_hours}\u00a0h/semaine`

/** La carte de « Début des cours » que la date vendue coche, s'il y en a une. */
const selectedStart = (quote) =>
    quote?.start_options?.[quote?.selected_start_option] ?? null

/** « sam 10 octobre » ; un devis d'avant ce champ n'a que son libellé long. */
const firstSessionShortLabel = (quote) =>
    quote?.first_session_short_label ||
    selectedStart(quote)?.first_session_short_label

export const firstSessionLabel = (quote) =>
    firstSessionShortLabel(quote) || quote?.first_session_label || "à définir"

/**
 * Le premier mois se prélève au premier cours, sauf si le jour de prélèvement
 * n'est pas encore passé ce mois-là : le devis le date alors, et c'est cette
 * date que la frise annonce — elle date des PAIEMENTS.
 */
const firstChargeTitle = (quote) =>
    quote.first_charge_on_label
        ? `Le ${quote.first_charge_on_label}`
        : firstSessionShortLabel(quote) || "Au premier cours"

const firstChargeDetail = (quote) => {
    if (!quote.first_session_month) return null
    if (!quote.first_month_period_label) {
        return `${quote.first_month_hours} h en ${quote.first_session_month}`
    }
    const hours =
        quote.first_month_hours === 1
            ? "L'heure de cours"
            : `Les ${quote.first_month_hours} h de cours`
    return `${hours} ${elide(quote.first_session_month)}, ${quote.first_month_period_label}`
}

/**
 * Le point du premier mois. À 1 h par semaine, un premier cours le dernier
 * jour du mois ne laisse aucune heure à facturer : le point reste, daté de ce
 * cours et sans montant, pour dire qu'il ne coûte rien de plus — « Les 0 h de
 * cours, 0 € » se lirait comme une erreur, et un point retiré laisserait le
 * parent se demander ce que ce cours lui coûte.
 */
const firstMonthPoint = (quote) =>
    quote.first_charge_cents === 0
        ? {
              key: "first_charge",
              title: firstSessionShortLabel(quote) || "Au premier cours",
              detail: quote.first_session_month
                  ? `Rien à régler pour ${quote.first_session_month}`
                  : "Rien à régler",
              amount: null,
          }
        : {
              key: "first_charge",
              title: firstChargeTitle(quote),
              detail: firstChargeDetail(quote),
              amount: euros(quote.first_charge_cents),
          }

/**
 * Les trois points de la frise des paiements : aujourd'hui, le premier mois,
 * puis la mensualité. `collectionDay` est le jour affiché, qui peut devancer
 * le devis le temps que la session rouverte le rende.
 */
export const paymentTimeline = (quote, collectionDay) => [
    {
        key: "deposit",
        title: "Aujourd'hui",
        detail: "Réservation",
        amount: euros(quote.deposit_cents),
    },
    firstMonthPoint(quote),
    {
        key: "monthly",
        title: `Le ${formatCollectionDay(collectionDay)} de chaque mois`,
        detail: quote.next_month_label
            ? `Mensualité habituelle à partir ${elide(quote.next_month_label)}`
            : "Mensualité habituelle",
        amount: monthlyPrice(quote),
    },
]

/** L'engagement que dit le devis, dans les clés de {@link commitmentChoices}. */
export const commitmentKey = (quote) =>
    quoteIsWithoutCommitment(quote) ? "no_commitment" : "commitment"

/**
 * Les deux engagements, dans la forme des cartes de « Début des cours »
 * (`{ key, title, date, caption }`, `date` étant la ligne forte). Sans
 * engagement, ce funnel ne vend qu'UNE formule, celle que la config annonce
 * (`noCommitment`) : la carte dit laquelle — ou, quand il lui manque quelque
 * chose (`gap`, tel que le rend `formulaGap`), ce qu'il faut d'abord régler à
 * l'étape formule, où la toucher ramène. Engagé, la carte dit ce que la
 * formule fait économiser (`savingLabel`, de `engagementSavingLabel`).
 */
const GAP_CAPTIONS = {
    subjects: "Ajustez d'abord vos matières",
    slots: "Ajoutez d'abord un créneau",
}

export const commitmentChoices = (
    noCommitment,
    gap = null,
    savingLabel = null
) => [
    {
        key: "commitment",
        title: "Engagement",
        date: COMMITTED,
        caption: savingLabel ?? COMMITMENT_COPY.firstMonth,
    },
    {
        key: "no_commitment",
        title: NOT_COMMITTED,
        date: `${noCommitment.weekly_hours} h par semaine`,
        caption:
            GAP_CAPTIONS[gap] ??
            `en ${formatNameOfOffer(noCommitment.offer_type)}`,
    },
]
